/**
 * Support Page
 *
 * Public support contact page.
 *
 * Primary responsibilities:
 * - Prefill support form values from query params
 * - Present the support form inside the redesigned marketing shell
 *
 * Dependencies:
 * - support API
 * - marketing primitives
 */

'use client'

import { ReactNode, useEffect, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  MarketingContainer,
  MarketingEyebrow,
  MarketingPage,
  MarketingPanel
} from '@/components/marketing/marketing-primitives'
import { GeneratedMarketingAsset } from '@/components/marketing/generated-marketing-asset'
import {
  MarketingAssetOuterFade,
  marketingAssetSoftEdgeClassName
} from '@/components/marketing/marketing-asset-outer-fade'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { getMarketingCopy } from '@/lib/marketing-copy'
import { cn } from '@/lib/utils'

const SUPPORT_CAPTCHA_TIMEOUT_MS = 15000

type SupportAltchaPayloadEvent = CustomEvent<{
  payload?: string
}>

type SupportAltchaStateChangeEvent = CustomEvent<{
  payload?: string
  state?: string
}>

// --------------

function readSupportAltchaPayload(event: Event) {
  return (event as SupportAltchaPayloadEvent).detail?.payload ?? ''
}

// --------------

function resolveSupportCaptchaPayload(
  widget: AltchaWidgetElement | null,
  currentPayload: string
) {
  if (currentPayload) {
    return Promise.resolve(currentPayload)
  }

  if (!widget || typeof widget.verify !== 'function') {
    return Promise.reject(
      new Error('Browser verification is still loading. Please try again.')
    )
  }

  return new Promise<string>((resolve, reject) => {
    let completed = false
    let timeoutId: number | undefined

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }

      widget.removeEventListener('verified', handleVerified)
    }

    const finish = (payload: string) => {
      if (completed) {
        return
      }

      completed = true
      cleanup()
      resolve(payload)
    }

    const fail = (message: string) => {
      if (completed) {
        return
      }

      completed = true
      cleanup()
      reject(new Error(message))
    }

    const handleVerified = (event: Event) => {
      const payload = readSupportAltchaPayload(event)

      if (!payload) {
        fail('Browser verification failed. Please try again.')
        return
      }

      finish(payload)
    }

    timeoutId = window.setTimeout(() => {
      fail('Browser verification timed out. Please try again.')
    }, SUPPORT_CAPTCHA_TIMEOUT_MS)

    widget.addEventListener('verified', handleVerified)
    widget.verify().catch(() => {
      fail('Browser verification failed. Please try again.')
    })
  })
}

export default function SupportPageClient() {
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)
  const searchParams = useSearchParams()
  const router = useRouter()
  const captchaRef = useRef<AltchaWidgetElement | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [captchaPayload, setCaptchaPayload] = useState('')

  useEffect(() => {
    const subject = searchParams.get('subject')
    const message = searchParams.get('message')

    if (!subject && !message) {
      return
    }

    setFormData((previousFormData) => ({
      ...previousFormData,
      subject: previousFormData.subject || subject || '',
      message: previousFormData.message || message || ''
    }))
  }, [searchParams])

  useEffect(() => {
    import('altcha').catch((error) => {
      console.error('Error loading support captcha:', error)
    })
  }, [])

  useEffect(() => {
    const widget = captchaRef.current
    if (!widget) {
      return
    }

    const handleVerified = (event: Event) => {
      setCaptchaPayload(readSupportAltchaPayload(event))
    }

    const handleStateChange = (event: Event) => {
      const detail = (event as SupportAltchaStateChangeEvent).detail

      if (detail?.state === 'verified') {
        setCaptchaPayload(detail.payload ?? '')
        return
      }

      setCaptchaPayload('')
    }

    widget.addEventListener('verified', handleVerified)
    widget.addEventListener('statechange', handleStateChange)

    return () => {
      widget.removeEventListener('verified', handleVerified)
      widget.removeEventListener('statechange', handleStateChange)
    }
  }, [])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target

    setFormData((previousFormData) => ({
      ...previousFormData,
      [name]: value
    }))
  }

  const resetSupportCaptcha = () => {
    setCaptchaPayload('')
    captchaRef.current?.reset?.()
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error(copy.common.supportFillAll)
      return
    }

    if (!formData.email.includes('@')) {
      toast.error(copy.common.supportEmailInvalid)
      return
    }

    setIsSubmitting(true)

    try {
      const captcha = await resolveSupportCaptchaPayload(
        captchaRef.current,
        captchaPayload
      )
      const response = await fetch('/api/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          captcha
        })
      })

      const payload = await response.json()

      if (!response.ok) {
        resetSupportCaptcha()
        throw new Error(payload.error || copy.common.supportSubmitError)
      }

      toast.success(copy.common.supportSuccess)
      router.push('/')
    } catch (error) {
      console.error('Error submitting support request:', error)
      resetSupportCaptcha()
      toast.error(error instanceof Error ? error.message : copy.common.supportSubmitError)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <MarketingPage>
      <MarketingContainer>
        <div className="grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
          <div className="flex max-w-[36rem] flex-col gap-5">
            <MarketingEyebrow>{copy.support.eyebrow}</MarketingEyebrow>
            <h1 className="font-display text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {copy.support.title}
            </h1>
            <p className="text-base leading-8 text-slate-600 dark:text-slate-300">
              {copy.support.description}
            </p>
            <ol className="mt-2 space-y-0 border-t border-slate-200/80 dark:border-white/10">
              {copy.support.includeItems.map(item => (
                <li
                  key={item}
                  className="border-t border-slate-200/80 py-4 first:border-t-0 dark:border-white/10"
                >
                  <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">{item}</p>
                </li>
              ))}
            </ol>
            <div className={cn("relative isolate mt-4 min-h-[240px] overflow-hidden sm:min-h-[300px]", marketingAssetSoftEdgeClassName)}>
              <GeneratedMarketingAsset
                assetKey="supportStack"
                frame={false}
                presentation="open"
                className="absolute inset-0"
                imageClassName="object-contain object-center opacity-95"
                priority
                sizes="(min-width: 1024px) 420px, 90vw"
              />
              <MarketingAssetOuterFade />
            </div>
          </div>

          <MarketingPanel className="self-start p-6 sm:p-7" interactive={false}>
            <form onSubmit={handleSubmit} className="grid gap-5">
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                className="hidden"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={copy.support.name} required>
                  <Input
                    id="name"
                    name="name"
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={copy.support.namePlaceholder}
                    className="h-12 rounded-2xl border-slate-200 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-400"
                  />
                </Field>

                <Field label={copy.support.email} required>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={copy.support.emailPlaceholder}
                    className="h-12 rounded-2xl border-slate-200 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-400"
                  />
                </Field>
              </div>

              <Field label={copy.support.subject} required>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={copy.support.subjectPlaceholder}
                  className="h-12 rounded-2xl border-slate-200 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-400"
                />
              </Field>

              <Field label={copy.support.message} required>
                <Textarea
                  id="message"
                  name="message"
                  rows={7}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={copy.support.messagePlaceholder}
                  className="rounded-3xl border-slate-200 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-400"
                />
              </Field>

              <altcha-widget
                ref={captchaRef}
                auto="onload"
                challengeurl="/api/support/captcha"
                hidefooter
                hidelogo
                name="captcha"
                refetchonexpire
                style={{
                  '--altcha-border-radius': '1rem',
                  '--altcha-color-base': 'transparent',
                  '--altcha-color-border': 'rgb(226 232 240)',
                  '--altcha-color-text': 'currentColor',
                  '--altcha-max-width': '100%'
                }}
              />

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => router.back()}
                  disabled={isSubmitting}
                  className="h-11 rounded-full border-slate-300 bg-white px-5 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                >
                  {copy.common.cancel}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={cn(
                    'h-11 rounded-full bg-slate-950 px-5 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white',
                    isSubmitting && 'cursor-not-allowed opacity-70'
                  )}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      {copy.common.sending}
                    </>
                  ) : (
                    copy.common.sendMessage
                  )}
                </Button>
              </div>
            </form>
          </MarketingPanel>
        </div>
      </MarketingContainer>
    </MarketingPage>
  )
}

function Field({
  label,
  required,
  children
}: {
  label: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <label className="grid gap-2 text-sm font-medium text-slate-900 dark:text-slate-100">
      <span>
        {label}
        {required ? <span className="ml-1 text-rose-500">*</span> : null}
      </span>
      {children}
    </label>
  )
}
